import http from 'hub-http/clients/apiClient';
import {
  registerQuery,
  useQuery,
  useDataFetchingClient,
  DataFetchingClient,
} from 'data-fetching-client';
import { BackendView, View } from '../../viewsSearch/types/ViewTypes';
import { transformBackendViewToInternalFormat } from '../../viewsSearch/dataOperators/backendViews';
import { SingleViewByIdQuery } from '../../singleView/hooks/useFetchSingleView';
import { makeQuickFetchRequest } from '../../utils/makeQuickFetchRequest';
import { generateDefaultPinnedViewQuickFetchName } from '../../quickFetch/defaultPinnedViewQuickFetch';

export const DEFAULT_PINNED_VIEW_QUERY_NAME = 'defaultPinnedView' as const;

export type FetchDefaultPinnedViewQueryType = {
  objectTypeId: string;
  namespace: string;
  viewId?: string;
  getClient?: () => DataFetchingClient;
};

export const DefaultPinnedViewQuery = registerQuery<
  typeof DEFAULT_PINNED_VIEW_QUERY_NAME,
  FetchDefaultPinnedViewQueryType,
  View
>({
  fieldName: DEFAULT_PINNED_VIEW_QUERY_NAME,
  args: ['objectTypeId', 'namespace', 'viewId', 'getClient'],
  fetcher: async ({ objectTypeId, namespace, viewId, getClient }) => {
    const url = `/sales/v4/views/default-view/${objectTypeId}?namespace=${namespace}${
      viewId ? `&viewId=${viewId}` : ''
    }`;

    const makeRequestFn = () => http.get<BackendView>(url);
    const quickFetchRequestName = generateDefaultPinnedViewQuickFetchName({
      objectTypeId,
      namespace,
      viewId,
    });
    const { requestPromise } = makeQuickFetchRequest<BackendView>({
      makeRequestFn,
      requestName: quickFetchRequestName,
    });
    const result = await requestPromise;
    const view = transformBackendViewToInternalFormat(result);
    const client = getClient && getClient();
    if (client) {
      client.writeQuery({
        data: { singleViewById: view },
        query: SingleViewByIdQuery,
        variables: {
          id: view.id,
          objectTypeId: view.objectTypeId,
          namespaceName: view.namespaceName,
        },
      });
    }
    return view;
  },
});

export type UseFetchDefaultPinnedViewQueryType = {
  objectTypeId: string;
  namespace: string;
  viewId?: string;
  skip?: boolean;
};

/**
 * Fetches the default pinned view for a given object type and namespace.
 * The view is cached in the Apollo cache so that subsequent fetches will return the cached value.
 * A default pinned view is the view the user should see first using the following fallback:
 * 1. The viewId provided in the query
 * 2. The first view in the list of pinned views
 * 3. The all view
 *
 * Permissions are checked as part of the fallback. This allows a single endpoint to fetch the
 * view to show the user.
 */
export const useFetchDefaultPinnedView = ({
  objectTypeId,
  namespace,
  skip,
  viewId,
}: UseFetchDefaultPinnedViewQueryType) => {
  const client = useDataFetchingClient();

  return useQuery(DefaultPinnedViewQuery, {
    variables: { objectTypeId, namespace, viewId, getClient: () => client },
    skip,
  });
};
